import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { Button, Combobox, Field, Input, type ComboboxOption } from '@/components/ui';
import {
  dateRangeWindow,
  describeTargetPeriod,
  describeTargetWindow,
  periodWindow,
  subdivideTargetPeriod,
  targetPeriodKindLabel,
  type TargetWindow,
} from '@/lib/targets';
import type { TargetWindowChoice } from '@/lib/use-my-targets';
import type { TargetPeriodRecord } from '@/lib/types';

/**
 * WHICH WINDOW A DASHBOARD IS REPORTING ON — one control, one derivation.
 *
 * ⚠️ **EXTRACTED FROM `UniversityPartnershipsPage` ON 2026-09-02, NOT COPIED.**
 * The Management Dashboard asks the same question of a group and of the
 * organisation that the partnerships page asks of one person, and two copies of
 * a window picker is two places that can disagree about what "Q2" means. The
 * rule it enforces — subdivisions are cuts OF THE PERIOD, never of the calendar
 * — is silent when it goes wrong: the figures still add up, to the wrong three
 * months.
 *
 * ⚠️ **THE STATE LIVES IN THE PARENT AND THE DERIVATION IS A PURE FUNCTION.**
 * Not a preference: the parent has to build its fetch from the same choice this
 * control renders, and a picker owning its own state would make the two reachable
 * only through a callback — which is how a request goes out for one window while
 * the control shows another.
 *
 * ── ✅ THREE STEPS, LEFT TO RIGHT — 2026-09-13, owner's instruction ──────────
 *
 * *"choose a cycle → choose the type (monthly / quarterly / annually / date
 * range) → choose the specific window."*
 *
 * ```
 *   Cycle              Report by              Month | Quarter | From + To
 *   F.Y. 2026 - 27     Monthly                Aug 2026 · 1 Aug – 31 Aug 2026
 * ```
 *
 * 🔴 **STEP ONE IS THE NEW HALF AND IT IS WHAT WAS MISSING, NOT THE DRILLDOWN.**
 * The drilldown was already here; both callers simply collapsed the period list
 * to ONE record through `periodForToday` and never exposed the list, so there
 * was nothing to choose FROM. What changed is that the two hooks now hand their
 * caller the whole list — see `periods` on `MyTargets` and on
 * `TargetPeriodsState` — and this control offers it.
 *
 * ⚠️ **THE THIRD STEP IS ABSENT UNDER `annually`, AND THAT IS THE FLOW WORKING
 * RATHER THAN A GAP.** Annually IS the whole cycle, so step two has already named
 * the window and a third control would be a list of one. The row therefore holds
 * two fields under `annually`, three under monthly and quarterly, and four under
 * a date range — which is why this component must stay LAST in its caller's
 * filter row. See the `inline` prop.
 *
 * ⚠️ **STEP ONE SITS FIRST INSIDE THIS COMPONENT, AND THE COMPONENT STILL SITS
 * LAST IN THE ROW.** Those are not in tension: the picker's growth is all at its
 * TAIL, so the fields that appear and disappear are still the last things in the
 * row and nothing after them moves. Putting the cycle first in the *row* instead
 * — ahead of the caller's own Currency control — would split this picker in two
 * around an unrelated field and separate step one from the steps it governs.
 */

/**
 * HOW FINELY the chosen cycle is cut — step two of the three.
 *
 * ⚠️ **`quarter` and `month` are subdivisions OF THE CHOSEN CYCLE PERIOD, not of
 * the calendar** — see `subdivideTargetPeriod`, which is where that is enforced
 * and where the reason is written down. Reading them as calendar quarters is the
 * mistake available here, and it is silent.
 *
 * 🔴 **`cycle` WAS RENAMED TO `annual`, VALUE AND ALL, ON 2026-09-13 — AND THE
 * RENAME IS COMPULSORY RATHER THAN TIDY.** *SUPERSEDED: `'cycle' | 'quarter' |
 * 'month' | 'range'`.* Step one of this picker is now a control literally called
 * **Cycle**, so a mode value spelled `cycle` would put one word on two different
 * things inside one component — the collision this codebase has already paid for
 * on `booked`/"Invoiced revenue" and on `reference-data`/"Entity Catalog". The
 * mode says HOW FINELY; the cycle says WHICH ONE.
 *
 * ⚠️ **CHANGING THE VALUE WAS SAFE BECAUSE THIS UNION NEVER LEAVES THIS FILE, AND
 * THAT WAS MEASURED RATHER THAN ASSUMED.** `grep -rn "WindowMode" src` returned
 * this module and nothing else on 2026-09-13; the values are never persisted,
 * never in a URL and never on the wire, and `windowChoiceOf` is the only thing
 * that reads one positively. Both callers hold a `WindowPickerState` they can
 * only have built through `initialWindowPickerState`. **Re-run that grep before
 * renaming another value** — the day one of these reaches a link or a stored
 * preference, a rename stops being free and becomes a migration.
 */
export type WindowMode = 'month' | 'quarter' | 'annual' | 'range';

/**
 * ⚠️ **OWNER'S ORDER AS WELL AS OWNER'S WORDS, 2026-09-13** — *"monthly /
 * quarterly / annually / date range"*. *SUPERSEDED: `cycle, quarter, month,
 * range`, which led with the default.* Shortest window first, which is also the
 * order `TARGET_PERIOD_KINDS` in `targets.ts` documents as the house rule for a
 * period-kind picker, so the two lists no longer read in opposite directions.
 *
 * ⚠️ **THE DEFAULT IS STILL `annual` AND IT IS KEYED BY VALUE, NOT BY POSITION**
 * — see `initialWindowPickerState`. Reordering this list cannot move the default
 * and must never be relied on to; a reader who wants the order changed back is
 * changing one line here and nothing else.
 */
const WINDOW_MODES: readonly WindowMode[] = ['month', 'quarter', 'annual', 'range'];

/**
 * ⚠️ **ADVERBS, NOT NOUNS, AND THE GRAMMAR IS WHAT THE CONTROL IS ASKING.**
 * Owner's words. This control chooses how the cycle is cut — *monthly* — where
 * the control AFTER it chooses which piece — *Aug 2026*. Labelling this one
 * `Month` made it read as a second window picker sitting beside the first, which
 * is exactly the confusion the three-step flow exists to remove.
 */
const WINDOW_MODE_LABEL: Record<WindowMode, string> = {
  month: 'Monthly',
  quarter: 'Quarterly',
  annual: 'Annually',
  range: 'Date range',
};

/**
 * The NOUN for ONE PIECE — the label and the search placeholder on step three.
 *
 * ⚠️ **A SECOND MAP RATHER THAN A REUSE OF THE ADVERBS ABOVE, deliberately.**
 * Under those labels step three would read "Quarterly: Q2 · 1 Aug – 31 Oct",
 * which asks its question in the wrong grammar; it wants "Quarter". Only the two
 * SUBDIVIDING modes have a piece to name, which is why this is keyed narrower
 * than `WindowMode` rather than exhaustively over it — `annual` names its window
 * at step two and `range` names it with two date boxes, so neither has a row in
 * here to be missing. A fifth subdividing mode is a compile error at this map and
 * at `chooseWindowMode`, which are the two halves of that decision.
 */
const SUBDIVISION_LABEL: Record<'month' | 'quarter', string> = {
  month: 'Month',
  quarter: 'Quarter',
};

/**
 * Step two's rows — "Report by".
 *
 * ⚠️ *SUPERSEDED 2026-09-13: "The kind picker's rows."* The word **kind** is
 * `TargetPeriodKind`'s — a catalogued period's own Month / Quarter / Year / Cycle
 * — and reusing it for this control is the collision the `cycle` → `annual`
 * rename above exists to stop, one level up in the prose.
 *
 * ⚠️ **DERIVED FROM `WINDOW_MODES` AND `WINDOW_MODE_LABEL`, never written out a
 * third time.** A hand-kept list would offer exactly the modes somebody
 * remembered on the day, so a fifth added to the union and not here becomes
 * unreachable while every derivation behind it works perfectly — and nothing
 * fails, because a missing row is a control that merely looks complete.
 */
const WINDOW_MODE_OPTIONS: ComboboxOption[] = WINDOW_MODES.map((value) => ({
  value,
  label: WINDOW_MODE_LABEL[value],
}));

/** Everything the picker holds. Owned by the parent — see the module comment. */
export interface WindowPickerState {
  /**
   * 🔴 **STEP ONE: WHICH CYCLE — AND `null` MEANS "THE CALLER'S DEFAULT", NEVER
   * "NO CYCLE".**
   *
   * ⚠️ **The two must not be collapsed and nothing may render this as an
   * absence.** `null` is *nobody has chosen yet*, and `windowChoiceOf` maps it to
   * `{kind: 'period'}` — the instruction both hooks have always understood as
   * *whatever period covers today*. **That is what makes this whole control
   * ADDITIVE**: until somebody touches the cycle, every request on both
   * dashboards is byte-for-byte the one that went out before step one existed,
   * and no figure anybody is already reading moves. Same shape, and the same
   * reason, as `TargetWindowChoice` naming three cases where two of them would
   * otherwise both be `null`.
   *
   * ⚠️ **THE WHOLE RECORD, NOT AN ID.** The subdivisions are cut from its
   * `startDate` and `endDate` and `periodWindow` needs its `name`, and
   * `windowChoiceOf` is a PURE function of this state — handed an id it would
   * have to look the row up, which means either a fetch inside a reducer or a
   * second copy of the period list in the parent to drift against the first.
   */
  period: TargetPeriodRecord | null;
  mode: WindowMode;
  /** The chosen quarter or month; `null` under `annual` and `range`. */
  subdivision: TargetWindow | null;
  rangeFrom: string;
  rangeTo: string;
}

export function initialWindowPickerState(): WindowPickerState {
  /*
   * ⚠️ **Opens on the caller's default period, reported ANNUALLY** — which is
   * precisely what this picker did before step one existed, so adding the cycle
   * control changed no opening figure on either dashboard. An annual figure is
   * what a dashboard headline answers and anything narrower is a narrowing
   * somebody asks for, the same rule `periodForToday` states for the period.
   */
  return { period: null, mode: 'annual', subdivision: null, rangeFrom: '', rangeTo: '' };
}

/**
 * A remembered `WindowPickerState` (filter memory, `useRememberedState`), or
 * `undefined` when what came back is not one — the caller then opens on
 * `initialWindowPickerState`.
 */
export function parseWindowPickerState(raw: unknown): WindowPickerState | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const value = raw as Record<string, unknown>;
  if (!WINDOW_MODES.includes(value.mode as WindowMode)) return undefined;
  if (typeof value.rangeFrom !== 'string' || typeof value.rangeTo !== 'string') return undefined;
  const period = value.period;
  if (period !== null) {
    if (typeof period !== 'object' || period === undefined) return undefined;
    const record = period as Record<string, unknown>;
    if (
      typeof record.id !== 'number' ||
      typeof record.startDate !== 'string' ||
      typeof record.endDate !== 'string'
    ) {
      return undefined;
    }
  }
  const subdivision = value.subdivision;
  if (subdivision !== null) {
    if (typeof subdivision !== 'object' || subdivision === undefined) return undefined;
    const window = subdivision as Record<string, unknown>;
    if (typeof window.from !== 'string' || typeof window.to !== 'string') return undefined;
  }
  return raw as WindowPickerState;
}

/**
 * A subdivision's identity in the picker. Its dates ARE what makes it unique.
 *
 * ⚠️ *SUPERSEDED 2026-09-13: "A subdivision's identity in a `<select>`."* — every
 * dropdown here is a `Combobox` now (owner's decision, below). The identity is
 * unchanged; only the control reading it moved.
 *
 * ⚠️ **It is the DATES and NOT the period's id, even now that step one has one.**
 * A subdivision is catalogued nowhere — `subdivideTargetPeriod` computes it — so
 * there is no id to use, and its dates are what make it unique within the cycle it
 * was cut from. Two cycles' quarters cannot collide, because two periods cannot
 * hold the same day twice at the same kind.
 */
function subdivisionValue(window: TargetWindow): string {
  return `${window.from}|${window.to}`;
}

/** A period's identity in the cycle picker. Its own id — it is a catalogued row. */
function periodValue(period: TargetPeriodRecord): string {
  return String(period.id);
}

/**
 * THE PERIOD IN PLAY: the chosen cycle, falling back to the caller's default.
 *
 * ⚠️ **ONE HELPER, CALLED BY EVERYTHING THAT SUBDIVIDES OR LABELS.** The reducer,
 * the subdivision option list, the third control's render and the cycle control's
 * own selected value must all agree about which period is in play. Two of them
 * reading the `period` prop while one reads `state.period` is how a control comes
 * to OFFER the quarters of one cycle and FETCH a quarter of another — which
 * produces a figure under the wrong label with nothing failing, the one outcome
 * this whole component is arranged to prevent.
 */
function activePeriod(
  state: WindowPickerState,
  fallback: TargetPeriodRecord | null,
): TargetPeriodRecord | null {
  return state.period ?? fallback;
}

/** The rows of the cycle control, and the records behind them, in one answer. */
interface CycleChoices {
  /** In offer order, with a pinned-back period FIRST. Resolves an `onChange`. */
  records: TargetPeriodRecord[];
  options: ComboboxOption[];
}

/**
 * The cycles on offer — **with the period in play PINNED BACK when it is not
 * among them.**
 *
 * 🔴 **THE PLACEHOLDER TRAP, AND ON THIS CONTROL IT IS THE ORDINARY CASE RATHER
 * THAN AN EDGE.** `Combobox` resolves its trigger from
 * `options.find((o) => o.value === value)` and falls back to its PLACEHOLDER, so
 * a held period absent from `options` makes the control read "Select…" over a
 * cycle the figures on screen are genuinely measured across. This file already
 * warned about that for a future caller of the subdivision control; step one
 * makes it reachable today, by two routes, and **neither is hypothetical**:
 *
 *   - **The default period NEED NOT BE A `cycle` AT ALL.** Both hooks take the
 *     WIDEST period covering today, of ANY `kind`, so a catalogued `year` row is
 *     a perfectly ordinary default — and the filter below drops it.
 *   - **A RETIRED period still reads back.** The list is fetched
 *     `isActive: true`, and a window somebody is already looking at does not stop
 *     being that window because an administrator retired it. Same rule as
 *     `withStoredOption()` on every other retired-value picker here.
 *
 * ⚠️ **PINNED *AND LABELLED*, NEVER PINNED SILENTLY.** The `hint` says which of
 * the two routes the row came down, because *"this is not one of the cycles"* and
 * *"this is a cycle nobody offers any more"* send a reader to different places —
 * one to the period catalogue, one to whoever retired it.
 *
 * ⚠️ **It is pinned FIRST rather than sorted in.** It is the row that is already
 * selected, so it is the row somebody opening the list is looking for, and the
 * same position `Combobox` gives its `addNew` action for the same reason.
 *
 * ⛔ **AN OPTION `hint` IS NOT HELPER TEXT AND A SWEEP MUST NOT TAKE IT.** It
 * labels one row inside a listbox rather than sitting under a control, which the
 * standing prohibition has never covered — `CLAUDE.md` says so in as many words,
 * naming "No country recorded" as the worked example.
 *
 * ⚠️ **THE LABEL IS THE NAME *AND THE DATES*, through `describeTargetPeriod`** —
 * the same ruling as the subdivision list below. A cycle's name is what somebody
 * recognises and its dates are what the figures are actually measured over, and
 * two catalogued periods may legitimately share a name at different kinds. The
 * dates are also what the filter box matches on, which is the point of being able
 * to type `2026-04` rather than hunt a row.
 */
function cycleChoices(
  periods: readonly TargetPeriodRecord[],
  held: TargetPeriodRecord | null,
  showAll: boolean,
): CycleChoices {
  /*
   * ⚠️ **`kind === 'cycle'` and NOT every catalogued period.** Step one asks
   * which CYCLE, and a `month` or `quarter` row offered here would be a cycle the
   * next two controls then cut into months — a window somebody can reach twice by
   * two different routes, under two different labels.
   */
  const everyCycle = periods.filter((candidate) => candidate.kind === 'cycle');
  /*
   * ⛔ **`showAll` WIDENS THE OFFER AND DECIDES NOTHING ELSE.** It is presentation
   * — which rows this listbox draws — and it must never reach the window the
   * panel measures, the pin, or a payload. The pin below still runs in both
   * states: widening the list is not the same as guaranteeing the held row is in
   * it, and on `showAll` a RETIRED or wrong-KIND cycle is still absent.
   */
  const offered = showAll ? everyCycle : cyclesAroundToday(everyCycle);
  const pin = held !== null && !offered.some((candidate) => candidate.id === held.id) ? held : null;
  const records = pin === null ? offered : [pin, ...offered];

  return {
    records,
    options: records.map((period) => ({
      value: periodValue(period),
      label: describeTargetPeriod(period),
      /*
       * ⚠️ **Compared by IDENTITY, not by index.** `pin` is the very record at the
       * head of `records`, so this cannot drift if the pinned row is ever sorted
       * in rather than prepended — where an `index === 0` test would then label
       * whichever cycle happened to sort first.
       */
      hint: period === pin ? pinnedCycleHint(period) : undefined,
    })),
  };
}

/**
 * THE THREE CYCLES STEP ONE OFFERS — the one running today, the one before it
 * and the one after. Owner's instruction, 2026-09-13.
 *
 * ⚠️ **ORDERED BY THE DATE WINDOW, NEVER BY `id`, and that is a measurement
 * rather than tidiness.** `2022 - 23` holds the HIGHEST id in the live table
 * (11) and is the EARLIEST window in it, so `MAX(id)` picks a financial year
 * that ended three years ago and a neighbour taken by id is a neighbour of
 * nothing. The ids are not contiguous either — 2, 6 and 7 are gone.
 *
 * ⚠️ **"Today" is compared as `YYYY-MM-DD` strings built from LOCAL parts**, the
 * same rule `currentCycle()` and `defaultSubdivision` already run on. A `Date`
 * built from a bare date string is midnight UTC and answers the previous day
 * west of Greenwich — on a boundary that is now 1 April, which is exactly the
 * day somebody would be checking this.
 *
 * 🔴 **NOTHING IS EVER MADE UNREACHABLE BY THIS.** A cycle outside the three is
 * still selectable whenever it is the one already chosen: `cycleChoices` pins
 * the held record back and labels it. That pairing is the whole reason this
 * narrowing is safe — a `Combobox` renders its PLACEHOLDER for a value absent
 * from its options, so narrowing alone would make a saved filter or a shared
 * link naming 2023-24 read as "no cycle chosen" over figures that are for
 * 2023-24. **Never narrow this without the pin.**
 *
 * ⚠️ **NO CYCLE CONTAINS TODAY IS AN ORDINARY STATE**, not an error: the
 * catalogue is one row somebody adds, and there is no rule that the windows
 * tile the calendar. The fallback walks to the next cycle that STARTS after
 * today — somebody looking at a gap is looking forward, not back — and falls
 * to the last catalogued cycle when every one of them is already over.
 */
function cyclesAroundToday(cycles: readonly TargetPeriodRecord[]): TargetPeriodRecord[] {
  /*
   * ⚠️ A fresh MUTABLE array on both paths, deliberately. `CycleChoices.records`
   * is mutable and `cycleChoices` spreads a pinned row onto the front of this;
   * handing back the readonly input would make that spread readonly too and the
   * error would surface at the caller rather than here.
   */
  if (cycles.length === 0) return [];

  const ordered = [...cycles].sort((a, b) =>
    a.startDate === b.startDate
      ? a.endDate.localeCompare(b.endDate)
      : a.startDate.localeCompare(b.startDate),
  );

  const today = todayIso();
  let index = ordered.findIndex((c) => c.startDate <= today && today <= c.endDate);
  if (index === -1) index = ordered.findIndex((c) => c.startDate > today);
  if (index === -1) index = ordered.length - 1;

  /*
   * `slice` clamps, so a current cycle at either end yields two rows rather than
   * three — which is the honest answer when there is no year before or after it
   * catalogued, and is what the first and last cycle in the table will do.
   */
  return ordered.slice(Math.max(0, index - 1), index + 2);
}

/**
 * Why a cycle row is in the list when the list does not offer it.
 *
 * ⚠️ **The kind is NAMED rather than merely flagged as wrong.** "Catalogued as a
 * year" tells somebody what to go and look at; "not a cycle" only tells them
 * something is off. `targetPeriodKindLabel` falls back to the server's own slug
 * for a kind this build does not know, so a widened enum reads as an unfamiliar
 * word rather than as a blank.
 *
 * ⚠️ **`isActive` is tested SECOND for a reason**, and the order is the message: a
 * retired period is dropped by the fetch whatever its kind, so "retired" is the
 * whole story there, while an ACTIVE row can only be missing because of its kind.
 * Reversing the two would tell somebody to go and re-catalogue a period that had
 * merely been retired.
 */
function pinnedCycleHint(period: TargetPeriodRecord): string {
  if (!period.isActive) return 'Retired — no longer offered';
  if (period.kind !== 'cycle') {
    return `Catalogued as ${targetPeriodKindLabel(period.kind).toLowerCase()}, not a cycle`;
  }
  /*
   * ⚠️ **THE THIRD REASON, ADDED WITH THE THREE-CYCLE NARROWING — and it is the
   * one a reader is most likely to meet.** An active cycle can now be missing
   * from the list for a reason that has nothing wrong with it: the list offers
   * the current cycle and its two neighbours, and this row is an older or later
   * one somebody arrived on. **It must not read as a fault** — the two sentences
   * above both send somebody off to fix something, and there is nothing to fix
   * here.
   */
  return 'Outside the three cycles offered';
}

/** Bare `YYYY-MM-DD` from LOCAL parts — `toISOString()` is the same UTC bug mirrored. */
function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
}

/**
 * Which subdivision a newly-chosen MODE — or a newly-chosen CYCLE — lands on: the
 * one containing today, falling back to the first.
 *
 * Today's piece is the useful default for the same reason a panel defaults to
 * the period covering today — somebody opening a dashboard is asking about now.
 * The fallback matters for a period that is entirely in the future or the past,
 * where no piece contains today and the first is the only non-arbitrary choice.
 *
 * 🔴 **THAT FALLBACK WAS THE RARE CASE AND IS NOW THE COMMON ONE, which is worth
 * knowing before anybody "improves" it.** ⚠️ *SUPERSEDED 2026-09-13, the premise
 * and NOT the conclusion: "Step one lets a reader choose any catalogued cycle".*
 * Step one now offers the current cycle and its two neighbours — see
 * {@link cyclesAroundToday} — **and the conclusion survives the narrowing
 * untouched, because two of those three are still wholly in the past or the
 * future.** Picking last year and then Monthly lands on that year's FIRST month,
 * not on a month near today. **That is the only non-arbitrary answer available**:
 * the nearest piece to today is a different window depending on which side of the
 * period today falls, and a reader who has just named a past cycle is not asking
 * about now.
 *
 * ⚠️ **The narrowing moved the RATIO, which is the half a later reader will get
 * wrong in the reassuring direction.** It used to be one cycle in eight that
 * contained today; it is now one in three, so the fallback fires on a smaller
 * share of choices than it did — **and it is still the majority**, which is the
 * only thing this note was ever claiming.
 */
function defaultSubdivision(windows: readonly TargetWindow[]): TargetWindow | null {
  const today = todayIso();
  return windows.find((w) => w.from <= today && today <= w.to) ?? windows[0] ?? null;
}

/**
 * The state after choosing a MODE, **including its default subdivision**.
 *
 * ⚠️ *SUPERSEDED 2026-09-13: "after choosing a kind".* **Kind** is
 * `TargetPeriodKind`'s word for a catalogued period's own shape, and step one now
 * offers those — see the rename at `WindowMode`.
 *
 * ⚠️ **Resolved HERE rather than in an effect on the parent.** A `useEffect`
 * that set the subdivision afterwards would render one frame with the mode
 * switched and no window selected, and would fetch twice.
 *
 * ⚠️ **It cuts the period IN PLAY, not the one passed in** — `activePeriod`, so
 * switching to Quarterly cuts the cycle the reader chose at step one and not
 * whatever covers today. Reading the argument directly is how step two comes to
 * offer the quarters of a cycle step three is not reporting on.
 */
export function chooseWindowMode(
  state: WindowPickerState,
  next: WindowMode,
  /** The caller's DEFAULT period. The chosen cycle wins — see `activePeriod`. */
  period: TargetPeriodRecord | null,
): WindowPickerState {
  if (next === 'quarter' || next === 'month') {
    const cut = activePeriod(state, period);
    return {
      ...state,
      mode: next,
      subdivision: defaultSubdivision(cut === null ? [] : subdivideTargetPeriod(cut, next)),
    };
  }
  return { ...state, mode: next, subdivision: null };
}

/**
 * The state after choosing a CYCLE, **including its default subdivision.**
 *
 * ⚠️ **RESOLVED HERE RATHER THAN IN AN EFFECT, for the same reason
 * `chooseWindowMode` resolves its own — and the reason is sharper on this one.**
 * A held subdivision is a cut of the cycle just LEFT, so its dates sit outside
 * the newly chosen cycle **entirely**: an effect that re-resolved afterwards
 * would render one frame showing last year's August under this year's cycle, and
 * would FETCH that window before correcting itself. One pure step, one window,
 * one request.
 *
 * ⛔ **`period` IS NOT NULLABLE, AND THAT IS A STRUCTURAL GUARD RATHER THAN an
 * oversight.** The cycle control carries no `emptyOptionLabel`, so "no cycle at
 * all" is not a thing a reader can pick, and `null` on `state.period` means
 * something else entirely — *the caller's default*, a value only
 * `initialWindowPickerState` may produce. A nullable parameter here would make
 * "un-choose the cycle" expressible, and `windowChoiceOf` would then hand the
 * panel silently back to whatever covers today while the control still read the
 * cycle's name. Same shape as `projectId` being absent from `DealFormModal`'s
 * `FormValues`: a guard somebody can forget is not a guard.
 *
 * ⚠️ **`range` KEEPS ITS TYPED DATES, DELIBERATELY.** A date range is not a cut of
 * anything — `dateRangeWindow` stamps it `periodId: null` and names it *Custom
 * range* — so the cycle above it is not consulted, and clearing somebody's typed
 * dates because they touched an unrelated control would throw away real work to
 * no purpose. `annual` has no subdivision to hold, so it needs nothing either;
 * only the two cutting modes do.
 */
export function chooseWindowPeriod(
  state: WindowPickerState,
  period: TargetPeriodRecord,
): WindowPickerState {
  const next: WindowPickerState = { ...state, period };
  if (state.mode !== 'quarter' && state.mode !== 'month') return next;
  return {
    ...next,
    subdivision: defaultSubdivision(subdivideTargetPeriod(period, state.mode)),
  };
}

/**
 * What to ask the server for.
 *
 * ⚠️ **`none` rather than falling back to the cycle, and the difference is the
 * whole reason `TargetWindowChoice` has three cases.** A half-typed range that
 * quietly reported the whole year would put a year's money on screen under a
 * picker reading "Date range" — a figure under the wrong label, which is the
 * failure this panel is arranged to prevent.
 */
export function windowChoiceOf(state: WindowPickerState): TargetWindowChoice {
  if (state.mode === 'annual') {
    /*
     * 🔴 **THE CHOSEN CYCLE REACHES THE REQUEST AS A CONCRETE WINDOW, AND THAT IS
     * HOW IT REACHES BOTH HOOKS' CACHE KEYS WITHOUT EITHER HOOK GAINING A TERM.**
     * `periodWindow` stamps the window with the period's own `id`, and both
     * `use-my-targets.ts` and `use-team-performance.ts` already key on
     * `periodId|from|to` — so switching cycle changes the key, the figures for the
     * cycle just left read as ABSENT, and the tiles fall back to their loading
     * state instead of sitting silently under the new cycle's name. A window
     * carrying an id is also asked for BY id, which is the exact question rather
     * than a date range that happens to coincide with it today.
     *
     * ⚠️ **`{kind: 'period'}` SURVIVES AND IS NOT DEAD CODE.** It is what an
     * UNTOUCHED picker emits, and it means *whatever period covers today* —
     * resolved inside the hook, which is the only thing that has read the period
     * list. Replacing it with a window here would require this control to hold the
     * list before its own first render, which it does not and cannot. It is also
     * what keeps step one additive: no request changes until somebody chooses.
     *
     * ⚠️ **Choosing the period the control was ALREADY DISPLAYING refetches
     * nothing**, which is why that is safe to let a reader do. The state moves
     * from `null` to that record, the choice moves from `{kind: 'period'}` to
     * `{kind: 'window'}` — and both resolve to the same `periodWindow(...)`, so
     * `windowKey` is unchanged and neither effect re-runs.
     */
    return state.period === null
      ? { kind: 'period' }
      : { kind: 'window', window: periodWindow(state.period) };
  }
  const chosen =
    state.mode === 'range' ? dateRangeWindow(state.rangeFrom, state.rangeTo) : state.subdivision;
  return chosen === null ? { kind: 'none' } : { kind: 'window', window: chosen };
}

/**
 * Whether the typed range runs backwards.
 *
 * ⚠️ **Told apart from a half-empty pair, not merged**: a half-empty pair is
 * somebody part-way through typing and gets a hint, an inverted pair is a
 * REFUSAL and gets an error bound to the control that fixes it.
 * `dateRangeWindow` returns `null` for both, which is right for the fetch and
 * wrong for the wording.
 */
export function rangeIsInverted(state: WindowPickerState): boolean {
  return state.rangeFrom !== '' && state.rangeTo !== '' && state.rangeFrom > state.rangeTo;
}

export function TargetWindowPicker({
  period,
  periods,
  state,
  onChange,
  inline = false,
  cycleToggleBelow = false,
}: {
  /**
   * The caller's DEFAULT period — what covers today.
   *
   * ⚠️ **It is the FALLBACK, not the authority: a chosen cycle wins.** See
   * `activePeriod`. `null` with nothing chosen disables everything but `annual`,
   * because every other mode is a cut of a period and there is none to cut.
   */
  period: TargetPeriodRecord | null;
  /**
   * 🔴 **THE CYCLES STEP ONE OFFERS — REQUIRED, AND DELIBERATELY NOT DEFAULTED TO
   * `[]`.** A default would render a cycle control holding exactly one pinned row
   * with no way to change it: a control that looks complete and can do nothing,
   * which is the affordance-with-one-outcome this app refuses everywhere else. A
   * caller that has not wired the list should fail at `tsc` rather than ship that.
   *
   * ⚠️ **Both hooks already read this list and neither exposed it**, which is why
   * step one was missing rather than badly built. Pass `myTargets.periods` or
   * `periodState.periods`; **do not fetch it again here** — a second read is a
   * second request per mount, and two copies of one list can disagree.
   *
   * ⚠️ *SUPERSEDED: "a second request per mount against a 120-per-60s per-IP
   * budget shared across the office."* It is **12,000/60s** (corrected
   * 2026-09-17). Taking the list as a prop is still right, and the reason that
   * survives is the second one: two copies of one list can disagree. The
   * budget that reason cited is off by a factor of a hundred.
   */
  periods: readonly TargetPeriodRecord[];
  state: WindowPickerState;
  onChange: (next: WindowPickerState) => void;
  /**
   * Render the fields BARE, with no row of its own.
   *
   * ⚠️ **A layout switch and nothing else.** The controls, their copy and their
   * state are identical either way — the only difference is whether this
   * component owns the bordered flex row or the parent does, so a page that
   * needs the window fields sitting beside its own "Team" field can put them in
   * one row rather than stacking two rows that ask one question between them.
   * Default `false`, so every existing caller renders exactly as before.
   *
   * ⚠️ **AN `inline` CALLER MUST PUT THIS LAST IN ITS ROW, and step one did not
   * change that.** The field count still swings between two and four as the mode
   * changes, and all of the swing is at this component's TAIL — the cycle control
   * is always rendered, so it never moves anything. A control placed after these
   * fields jumps position when an unrelated one is touched, which reads as a
   * layout fault. Both callers say so at their own call site.
   */
  inline?: boolean;
  /**
   * Put the "Show all N cycles" toggle UNDER the Cycle list as a small text
   * link, instead of beside it as a ghost button (owner, 2026-10-11, on the
   * University Partnerships panel). Default `false`, so the Management
   * Dashboard is unchanged: its filter bar aligns fields to their BOTTOM edge,
   * and a taller Cycle field there would push its label out of line.
   */
  cycleToggleBelow?: boolean;
}) {
  /*
   * ⚠️ **THE CHOSEN CYCLE, FALLING BACK TO THE CALLER'S DEFAULT — resolved ONCE
   * for the whole render.** Every control below reads this and not `period`, so
   * the cycle control's own selected row, the subdivision list and the reducer
   * cannot disagree about which period is in play.
   */
  const cycle = activePeriod(state, period);

  /*
   * ⚠️ **THE NARROWED MODE IS HOISTED INTO A CONST BECAUSE A `&&` GUARD DOES NOT
   * SURVIVE A RENDER PROP.** `state.mode` is a property of a mutable object, so
   * TypeScript widens it back to the whole `WindowMode` union the moment the
   * JSX crosses into `Field`'s `{({ id }) => …}` callback — which is why the
   * label on the `Field` itself typechecks and the identical lookup three lines
   * inside it does not. A const cannot be reassigned, so its narrowing holds
   * across the boundary.
   *
   * ⛔ **The alternative is a cast, and it is the wrong one here.** The cast at
   * the mode `Combobox` below is safe because it asserts something about a
   * value the component itself produced; a cast here would assert `state.mode`
   * is one of two members at a point where nothing in scope says so, which is
   * exactly the shape that goes wrong when a fifth mode is added to the union.
   * This way the compiler names this line instead.
   */
  const subdivisionMode =
    state.mode === 'quarter' || state.mode === 'month' ? state.mode : null;

  /*
   * Recomputed each render rather than memoised: it is twelve small objects off
   * one row, and nothing downstream uses it as an effect dependency, so a memo
   * would buy nothing and add a stale-closure hazard.
   */
  const subdivisions =
    cycle !== null && subdivisionMode !== null ? subdivideTargetPeriod(cycle, subdivisionMode) : [];

  /*
   * ✅ **THE WAY BACK TO AN OLDER CYCLE — owner's decision, 2026-09-13, taken on
   * a measurement that falsified the first ruling.** Step one offers three
   * cycles; the pin keeps an already-chosen fourth selectable. **That pin turned
   * out to be UNREACHABLE**: this picker has no URL key — both callers hold the
   * choice in plain `useState` — so no saved filter and no shared link can ever
   * put an outside cycle into state, and `state.period` starts `null`. The
   * narrowing was therefore a hard one: five of the eight catalogued cycles had
   * no route at all, with nothing on screen saying why.
   *
   * ⚠️ **LOCAL STATE, AND DELIBERATELY NOT LIFTED TO THE PARENT.** This file's
   * own rule — *the state lives in the parent and the derivation is a pure
   * function* — is about the WINDOW, because the parent has to build its fetch
   * from the same choice this control renders. **How long the list is decides no
   * request**, so lifting it would widen a prop for a presentation flag and give
   * a caller a way to disagree with the listbox in front of them.
   *
   * ⚠️ **It does NOT reset when the cycle changes**, on purpose: somebody who
   * widened the list is browsing history, and snapping it shut under them after
   * every pick is the control fighting the user — the same failure a re-seeding
   * document name has.
   */
  const [showAllCycles, setShowAllCycles] = useRememberedState('window-picker-show-all-cycles', false);
  const cycles = cycleChoices(periods, cycle, showAllCycles);
  /*
   * ⚠️ **Compared against the OFFER, not against a constant three.** The offer is
   * two rows at either end of the catalogue and can carry a pinned row, so a
   * hardcoded 3 would offer "Show all" over a list that already holds everything.
   */
  const everyCycleCount = periods.filter((candidate) => candidate.kind === 'cycle').length;
  const cyclesAreNarrowed = !showAllCycles && everyCycleCount > cycles.records.length;

  const inverted = rangeIsInverted(state);

  const fields = (
    <>
      {/*
        * ✅ **EVERY DROPDOWN HERE IS A SEARCHABLE `Combobox` — owner's decision,
        * 2026-09-13.** *SUPERSEDED: two `ui/Select`s with inline `<option>`
        * lists.* Every non-table-column filter dropdown in the app takes one
        * shape, and the owner chose that consistency explicitly over the "a
        * handful of options needs no search box" reading. The subdivision list
        * below is where it earns its keep on its own merits — up to twelve
        * months, more if a period ever runs past a year — and step one earns it
        * again as the catalogue fills with one period per financial cycle.
        *
        * ⚠️ *SUPERSEDED the same day: "**BOTH CONTROLS HERE**".* **There are
        * THREE dropdowns now**, and the sentence was left behind by the control
        * added directly beneath it — which is why this reads "every" rather than
        * a count. **Count the `<Combobox` tags, never this paragraph**: a number
        * in prose about a thing somebody can add is the shape this repo keeps
        * recording, and it went wrong here within hours of being written.
        *
        * ⚠️ **`Combobox` takes no `className`, so the width is set on a
        * wrapper** — the same shape `FinancialAnalyticsPanel` already uses for
        * its `MultiCombobox`. `max-w-full` is what keeps the fixed width from
        * overflowing the bar at 320px.
        *
        * ⛔ **NO CONTROL HERE HAS AN `emptyOptionLabel`, and that is the whole of
        * what was NOT changed.** Neither `<select>` carried a blank `<option>`,
        * so adding one would make "no window at all" a thing a reader can pick —
        * which `windowChoiceOf` maps to `{kind: 'none'}` and every caller renders
        * as a blocked panel. On step one it would be worse still: `null` on
        * `state.period` already means *the caller's default*, so a blank row would
        * give one value two opposite meanings — see `WindowPickerState`.
        */}
      {/*
        * 🔴 **STEP ONE — 2026-09-13, owner's instruction. THE CONTROL THAT WAS
        * MISSING.**
        *
        * ⚠️ **IT SHOWS THE PERIOD IN PLAY EVEN BEFORE ANYBODY CHOOSES ONE, and
        * that is honesty rather than a pre-selection.** With `state.period` still
        * `null` the figures ARE measured over the caller's default period, so a
        * trigger reading "Select…" beside them would be the placeholder trap
        * arriving at first paint — the panel naming a cycle in its figures while
        * step one of the flow denied knowing one. `activePeriod` is what makes the
        * two agree, and `cycleChoices` is what guarantees the row exists to be
        * shown.
        *
        * ⚠️ **UNDER `Date range` THIS CONTROL IS NOT CONSULTED, and it stays
        * ENABLED anyway.** A typed range is not a cut of a cycle
        * (`periodId: null`, named *Custom range*), so the cycle genuinely decides
        * nothing there — but disabling it would be a refusal this dense row has
        * nowhere to explain, and it would force the cycle to be re-chosen on the
        * way back to Monthly. **Do not "fix" it by disabling or hiding it**; it is
        * step one of a flow and the flow must not lose its first step when the
        * reader reaches its fourth option.
        */}
      <Field label="Cycle">
        {({ id }) => (
          /*
            The show-all button sits BESIDE the list, wrapping under it only
            when there is no room, so the Cycle field is the same height as its
            neighbours and their labels line up.
          */
          <div
            className={
              cycleToggleBelow
                ? 'flex max-w-full flex-col items-start gap-1'
                : 'flex max-w-full flex-wrap items-center gap-x-1 gap-y-1'
            }
          >
          <div className="w-72 max-w-full">
            <Combobox
              id={id}
              value={cycle === null ? '' : periodValue(cycle)}
              options={cycles.options}
              searchPlaceholder="Search cycles…"
              /*
               * ⚠️ **Disabled only when there is genuinely nothing to choose** —
               * the list unread, or catalogued with no cycle at all and no default
               * to pin back. Once either exists there is at least one row, so this
               * is the loading and the truly-empty state and nothing else.
               *
               * ⚠️ **`disabled`, NOT `Combobox`'s `loading`**, for the same reason
               * the control below it keeps `disabled`: this state is local to the
               * parent and never URL-backed, so there is no arriving value to keep
               * clearable. See the note on that control — a later harmonising pass
               * must not swap either.
               */
              disabled={cycles.options.length === 0}
              onChange={(value) => {
                /*
                 * ⚠️ **Resolved against `cycles.records`, which INCLUDES the pinned
                 * row.** Matching against `periods` alone would silently ignore a
                 * click on the one row that is always present — the period the
                 * panel is already measuring — and the control would read as
                 * broken on the only cycle a reader can see today.
                 *
                 * A miss returns without calling `onChange`: `Combobox` only ever
                 * emits the `value` of a row it rendered, so this is unreachable,
                 * and standing still is the right answer to an impossible click.
                 * Falling back to a cycle would CHANGE the window nobody asked to
                 * change.
                 */
                const picked = cycles.records.find((p) => periodValue(p) === value);
                if (picked === undefined) return;
                onChange(chooseWindowPeriod(state, picked));
              }}
            />
          </div>
            {/*
              * ⛔ **A BUTTON BESIDE THE LIST, NEVER A ROW INSIDE IT.** `Combobox`
              * cannot express a disabled or decorative option — every row it
              * draws is Enter-selectable — so a "Show all cycles…" row would
              * become a pickable VALUE, and `windowChoiceOf` would be handed a
              * period id that names no period. This file already records that
              * trap under `emptyOptionLabel`, and `IntakesPage` paid for it once.
              *
              * ⚠️ **NOT HELPER TEXT.** It is a control with a label, and its
              * label states the outcome of pressing it rather than explaining
              * the control beside it. The count is in the label because *"Show
              * all"* over a list already showing everything is the
              * affordance-with-one-outcome this repo forbids — which is also why
              * it is withheld entirely when nothing is hidden.
              */}
            {(cyclesAreNarrowed || showAllCycles) && cycleToggleBelow && (
              <button
                type="button"
                className="text-xs text-fg-muted underline-offset-2 transition-colors duration-150 ease-out hover:text-fg hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring motion-reduce:transition-none"
                onClick={() => setShowAllCycles((shown) => !shown)}
              >
                {showAllCycles ? 'Show recent cycles' : `Show all ${everyCycleCount} cycles`}
              </button>
            )}
            {(cyclesAreNarrowed || showAllCycles) && !cycleToggleBelow && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAllCycles((shown) => !shown)}
              >
                {showAllCycles ? 'Show recent cycles' : `Show all ${everyCycleCount} cycles`}
              </Button>
            )}
          </div>
        )}
      </Field>

      {/*
        * ⚠️ **RELABELLED FROM "Target period" ON 2026-09-13, AND THE OLD LABEL IS
        * NOW ACTIVELY WRONG RATHER THAN MERELY VAGUE.** The control beside it is a
        * target period; this one is how finely that period is cut. Two rejected
        * candidates, recorded so they are not re-tried: **"Period type"** collides
        * with `TargetPeriodKind`, whose own labels are Month / Quarter / Year /
        * Cycle for a *catalogued row's* kind — a different fact wearing the same
        * words — and **"Breakdown"** is already what the member table below is
        * called on the Financial Analytics tab.
        */}
      <Field label="Report by">
        {({ id }) => (
          <div className="w-40 max-w-full">
            <Combobox
              id={id}
              value={state.mode}
              options={WINDOW_MODE_OPTIONS}
              /*
               * Disabled until there is a period to subdivide: every mode but
               * `annual` is a cut of one, so offering them first would let
               * somebody choose a quarter of nothing.
               *
               * ⚠️ **Tested against the period IN PLAY, not against the prop** —
               * a chosen cycle is a period to cut even where nothing covers today,
               * which is reachable the moment a catalogued cycle runs adrift of
               * the calendar.
               *
               * ⚠️ **STILL `disabled`, deliberately NOT `Combobox`'s `loading`.**
               * `DealsPage` swapped the two on the same day for its reference
               * masters, because there a value already in the URL had to stay
               * clearable while the list arrived. Neither applies here: this
               * state is local to the parent, never URL-backed, and the refusal
               * is "there is no period to cut", not "the list is on its way". A
               * later harmonising pass must not copy that swap across.
               */
              disabled={cycle === null}
              /*
               * The cast is as safe as the `<select>`'s was and for the same
               * reason: `Combobox` only ever calls `onChange` with the `value`
               * of a row it rendered, and every row here comes from
               * `WINDOW_MODES`. With no `emptyOptionLabel` there is no `''` row
               * for it to emit either.
               */
              onChange={(value) => onChange(chooseWindowMode(state, value as WindowMode, period))}
            />
          </div>
        )}
      </Field>

      {subdivisionMode !== null && (
        /*
         * ⚠️ **STEP THREE, AND IT IS LABELLED WITH THE NOUN.** `SUBDIVISION_LABEL`
         * rather than `WINDOW_MODE_LABEL`, which now holds adverbs — "Quarterly:
         * Q2 · 1 Aug – 31 Oct" asks its question in the wrong grammar.
         */
        <Field label={SUBDIVISION_LABEL[subdivisionMode]}>
          {({ id }) => (
            <div className="w-72 max-w-full">
              <Combobox
                id={id}
                value={state.subdivision === null ? '' : subdivisionValue(state.subdivision)}
                /*
                 * 🔴 **THE LABEL NO LONGER CARRIES DAY-LEVEL DATES — owner's
                 * instruction, 2026-09-13.** *SUPERSEDED: "The label is the
                 * DATES, not just `Q2` or `May 2026`. A piece at either end of
                 * the period is CLAMPED to it … and a bare `Q2` never said
                 * which cycle it belonged to. It is also what the filter box
                 * matches on, which is the point of typing `2026-07`."*
                 *
                 * **`describeTargetWindow` still answers both of the real
                 * grounds and answers them per window** — a clamped piece keeps
                 * its exact dates, and a quarter reads `Q2 · Jul – Sep 2026` —
                 * so what went is the case where the name was already exact and
                 * the dates repeated it. **The third ground was never true:**
                 * the label rendered `Jul 1, 2026`, never `2026-07`, so that
                 * search could not have worked.
                 */
                options={subdivisions.map((w) => ({
                  value: subdivisionValue(w),
                  label: describeTargetWindow(w),
                }))}
                /*
                 * ⚠️ **`SUBDIVISION_LABEL`, not `WINDOW_MODE_LABEL`.** The adverbs
                 * pluralise to "Search monthlys…", which is the kind of string a
                 * label rename produces at a call site three lines away and
                 * nothing typechecks.
                 */
                searchPlaceholder={`Search ${SUBDIVISION_LABEL[subdivisionMode].toLowerCase()}s…`}
                /*
                 * ⚠️ **`''` IS REACHABLE HERE AND FALLS BACK TO THE PLACEHOLDER
                 * — where the `<select>` fell back to a blank box.** Both mean
                 * the same thing, and `state.subdivision` is `null` only when the
                 * period in play yielded no pieces to cut.
                 *
                 * 🔴 **THE OTHER ROUTE THIS WARNED ABOUT IS NOW REACHABLE, AND IT
                 * IS CLOSED IN THE REDUCER RATHER THAN HERE — 2026-09-13.**
                 * *SUPERSEDED: "it can only fall OFF this list if `period` changes
                 * under a mode that was chosen from the previous one. Neither
                 * caller does that … **If a caller ever seeds this state from a
                 * link, the held window has to be injected back into `options` the
                 * way the two revenue panels inject a billing region**."* Step one
                 * changes the period under a mode chosen from the previous cycle
                 * **every time somebody uses it**, and a cut of last year's cycle
                 * is not merely absent from this list — its dates sit outside the
                 * new cycle entirely, so pinning it back would show a window the
                 * figures are NOT measured across. **`chooseWindowPeriod`
                 * re-resolves the subdivision in the same pure step**, so the held
                 * window is always a cut of the period in play and can never fall
                 * off this list at all.
                 *
                 * ⚠️ **The pinning advice DID come true, on step one**, where a
                 * held period genuinely still belongs on screen — see
                 * `cycleChoices`. Two controls, two opposite right answers:
                 * re-resolve a window that has stopped meaning anything, pin back a
                 * period that still does.
                 *
                 * ⚠️ **A URL-seeded window would still need the injection** — no
                 * caller seeds one, both hold this in `useState`. That half of the
                 * superseded note stands.
                 */
                onChange={(value) =>
                  onChange({
                    ...state,
                    subdivision: subdivisions.find((w) => subdivisionValue(w) === value) ?? null,
                  })
                }
              />
            </div>
          )}
        </Field>
      )}

      {state.mode === 'range' && (
        <>
          <Field label="From">
            {({ id }) => (
              <Input
                id={id}
                type="date"
                className="w-44"
                value={state.rangeFrom}
                onChange={(event) => onChange({ ...state, rangeFrom: event.target.value })}
              />
            )}
          </Field>
          {/*
            * ⚠️ **THE `error` STAYS AND THE `hint` IS GONE — owner's ruling,
            * 2026-09-05.** `error` is a validation message, which is a keeper;
            * the "Both dates are needed." hint was advice printed in advance of
            * any refusal, so it went with the rest of this app's helper text.
            */}
          <Field
            label="To"
            error={inverted ? 'Choose an end date on or after the start date.' : undefined}
          >
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                type="date"
                className="w-44"
                aria-describedby={describedBy}
                invalid={inverted}
                value={state.rangeTo}
                onChange={(event) => onChange({ ...state, rangeTo: event.target.value })}
              />
            )}
          </Field>
        </>
      )}
    </>
  );

  if (inline) return fields;

  return (
    <div className="flex flex-wrap items-start gap-3 border-b border-border px-4 py-3">{fields}</div>
  );
}
